import type { DocNode } from '@atlaskit/adf-schema/doc';

import type { RefreshLinkUrlHandler } from '../ConnectionsByLineMenu.tsx';
import type { AtAGlanceStructuralAncestor } from '../at-a-glance/atAGlanceTypes.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import { createPanelDataBridge, type PanelDataBridge } from './createPanelDataBridge.ts';

const noopRefreshLinkUrl: RefreshLinkUrlHandler = async () => undefined;

export type OutgoingLinksBridgeData = {
	contentId: string | undefined;
	pageAdf: DocNode | null;
	pageVersion: number | null | undefined;
	pageUpdatedAt: string | null | undefined;
	structuralAncestors?: readonly AtAGlanceStructuralAncestor[] | null;
	outgoingLinks: EnhancedSmartLink[];
	refreshLinkUrl: RefreshLinkUrlHandler;
};

export const initialOutgoingLinksBridgeData: OutgoingLinksBridgeData = {
	contentId: undefined,
	pageAdf: null,
	pageVersion: undefined,
	pageUpdatedAt: undefined,
	outgoingLinks: [],
	refreshLinkUrl: noopRefreshLinkUrl,
};

type OutgoingLinksBridge = PanelDataBridge<OutgoingLinksBridgeData>;

/**
 * Carries the page ADF, version, update time, and smart links the connections byline has already
 * collected. See `createPanelDataBridge` for why the panel needs a bridge at all.
 *
 * The byline is the only producer: collecting the links needs the page ADF from
 * `RendererActionsContext` (or the editor API), which is only in scope deep inside
 * the content renderer. (`refreshLinkUrl` is carried so a row can be upgraded after
 * a Connect flow, but no panel section consumes it yet — the panel offers no
 * Connect action.)
 *
 * Only ever written from an effect, so nothing publishes during SSR and this
 * module-level state cannot leak between server requests.
 */
const outgoingLinksBridge: OutgoingLinksBridge = createPanelDataBridge(
	initialOutgoingLinksBridgeData,
);

// Annotated because `isolatedDeclarations` cannot infer a re-exported binding.
export const publishOutgoingLinks: OutgoingLinksBridge['publish'] = outgoingLinksBridge.publish;
export const clearOutgoingLinks: OutgoingLinksBridge['clear'] = outgoingLinksBridge.clear;
export const subscribeToOutgoingLinks: OutgoingLinksBridge['subscribe'] =
	outgoingLinksBridge.subscribe;
export const useOutgoingLinks: OutgoingLinksBridge['useValue'] = outgoingLinksBridge.useValue;
export const clearOutgoingLinksForTesting: OutgoingLinksBridge['clearForTesting'] =
	outgoingLinksBridge.clearForTesting;
